import {atoms, Box} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {ChevronDownLine, ChevronRightLine} from '@dropbox/dig-icons/assets';
import {RedactedWorkstreamMetric, WorkstreamBasicWithSummary, WorkstreamMetric} from 'client';
import {Link} from 'components/DSYS/Link';
import {OptionalTooltip} from 'components/DSYS/OptionalTooltip';
import {Table} from 'components/DSYS/Table';
import {TableGroupHeaderSection} from 'components/DSYS/TableGroupHeaderSection';
import {tableStickyLayoutDataProps} from 'components/DSYS/tableStickyLayout';
import {ROUTE_PATHS} from 'constant';
import {t} from 'i18next';
import {type CSSProperties, Fragment, type ReactNode, useMemo, useState} from 'react';
import {useWorkstreamDetail} from 'views/workstreams/detail/hooks';
import {useWorkstreamMetricMutation} from 'views/workstreams/hooks';
import {
  getNorthStarMetricOrdering,
  isFullWorkstreamMetric,
  isWorkstreamStatusArchived,
} from 'views/workstreams/utils';

import type {MetricsGrain} from './MetricsGrainControls';
import {logWorkstreamMetricsSubworkstreamsExpanded} from './workstreamMetricAnalytics';
import {
  ANNUAL_METRIC_COLUMNS,
  ANNUAL_TABLE_CLASS_NAME,
  AnnualMetricRow,
  getAnnualColumnHeaderLabel,
} from './WorkstreamMetricsAnnualTable';
import {
  getQuarterColumnHeaderLabel,
  getQuarterMetricColumns,
  type MetricQuarter,
  QUARTER_METRIC_COLUMN_COUNT,
  QuarterTableMetricRow,
  SubWorkstreamHeaderRow,
} from './WorkstreamMetricsQuarterTable';
import type {WorkstreamMetricChangeHandler} from './WorkstreamRedesignSections';

// Headers stick to the top of the metrics scroll pane (not below the page chrome),
// so the page-chrome sticky offset is zeroed; the table still publishes its own
// header height so group headers pin just beneath the column header.
const PANE_STICKY_VARS = {'--dsys-sticky-offset-top': '0px'} as CSSProperties;

type ScorecardMetric = WorkstreamMetric | RedactedWorkstreamMetric;

const onlyMetrics = (metrics: WorkstreamBasicWithSummary['metrics'] | undefined | null) =>
  (metrics ?? []).filter((metric): metric is ScorecardMetric => metric.type === 'metric');

const ScorecardMetricRow = ({
  grain,
  metric,
  year,
  quarter,
  workstreamId,
  handleMetricChange,
}: {
  grain: MetricsGrain;
  metric: ScorecardMetric;
  year: number;
  quarter: MetricQuarter;
  workstreamId: number;
  handleMetricChange: WorkstreamMetricChangeHandler;
}) =>
  grain === 'quarterly' ? (
    <QuarterTableMetricRow
      metric={metric}
      year={year}
      quarter={quarter}
      workstreamId={workstreamId}
      handleMetricChange={handleMetricChange}
    />
  ) : (
    <AnnualMetricRow
      metric={metric}
      year={year}
      workstreamId={workstreamId}
      handleMetricChange={handleMetricChange}
    />
  );

/**
 * One workstream's grouped section inside the shared scorecard table: a sticky
 * header (parent name + metric count) followed by its metric rows and any direct
 * sub-workstream rows. Sub-workstream metrics are not in the list payload, so they
 * are lazily fetched only for workstreams that have children.
 */
const ScorecardGroup = ({
  grain,
  workstream,
  year,
  quarter,
  includeArchived,
}: {
  grain: MetricsGrain;
  workstream: WorkstreamBasicWithSummary;
  year: number;
  quarter: MetricQuarter;
  includeArchived: boolean;
}) => {
  const {updateMetric} = useWorkstreamMetricMutation();
  const hasDependencies = (workstream.dependencies_count ?? 0) > 0;
  const {data: detail} = useWorkstreamDetail(hasDependencies ? workstream.id : undefined);
  // A single chevron next to the workstream name shows/hides all of its
  // sub-workstreams (separator rows and metrics) at once. Collapsed by default so
  // the list opens on each workstream's own outcomes.
  const [subWorkstreamsExpanded, setSubWorkstreamsExpanded] = useState(false);
  const columnCount =
    grain === 'quarterly' ? QUARTER_METRIC_COLUMN_COUNT : ANNUAL_METRIC_COLUMNS.length;

  const metrics = useMemo(() => onlyMetrics(workstream.metrics), [workstream.metrics]);
  const {orderedMetrics} = useMemo(
    () =>
      metrics.every(isFullWorkstreamMetric)
        ? getNorthStarMetricOrdering(metrics, workstream.id)
        : {orderedMetrics: metrics},
    [metrics, workstream.id]
  );

  const subWorkstreamMetricGroups = useMemo(
    () =>
      [...(detail?.dependencies ?? [])]
        .filter(({dependent}) => includeArchived || !isWorkstreamStatusArchived(dependent))
        .sort((a, b) => a.order - b.order)
        .map((dependency) => ({
          id: dependency.dependent.id,
          name: dependency.dependent.name,
          metrics: (() => {
            const groupMetrics = onlyMetrics(dependency.dependent.metrics);
            return groupMetrics.every(isFullWorkstreamMetric)
              ? getNorthStarMetricOrdering(groupMetrics, dependency.dependent.id).orderedMetrics
              : groupMetrics;
          })(),
        }))
        .filter((group) => group.metrics.length > 0),
    [detail?.dependencies, includeArchived]
  );

  const totalMetricCount =
    metrics.length +
    subWorkstreamMetricGroups.reduce((sum, group) => sum + group.metrics.length, 0);

  const handleMetricChange = async (
    targetWorkstreamId: number,
    metricList: ScorecardMetric[],
    metricId: number,
    update: Partial<WorkstreamMetric>
  ) => {
    const existingMetric = metricList.find(
      (metric): metric is WorkstreamMetric =>
        isFullWorkstreamMetric(metric) && metric.id === metricId
    );
    if (!existingMetric) return;
    await updateMetric(targetWorkstreamId, metricId, existingMetric, update);
  };
  if (totalMetricCount === 0) {
    return null;
  }

  const hasSubWorkstreams = subWorkstreamMetricGroups.length > 0;
  const toggleSubWorkstreams = () =>
    setSubWorkstreamsExpanded((prev) => {
      const next = !prev;
      logWorkstreamMetricsSubworkstreamsExpanded('workstreams_scorecard', {
        workstream_id: workstream.id,
        expanded: next,
        sub_workstream_count: subWorkstreamMetricGroups.length,
      });
      return next;
    });
  const nameLink = (
    <Link
      to={ROUTE_PATHS.WORKSTREAMS_DETAIL.replace(':id', String(workstream.id))}
      monochromatic
      hasNoUnderline
      showUnderlineOnHover
    >
      {workstream.name}
    </Link>
  );

  return (
    <TableGroupHeaderSection
      sectionKey={`ws-${workstream.id}`}
      stickyMode="native"
      columnCount={columnCount}
      compactHeaderPadding
      label={
        hasSubWorkstreams ? (
          <span style={{display: 'inline-flex', alignItems: 'center', gap: 6}}>
            <button
              type="button"
              onClick={toggleSubWorkstreams}
              aria-expanded={subWorkstreamsExpanded}
              aria-label={
                subWorkstreamsExpanded ? t('hide_sub_workstreams') : t('show_sub_workstreams')
              }
              className={atoms({display: 'inline-flex', alignItems: 'center'})}
              style={{padding: 0, background: 'none', border: 'none', cursor: 'pointer'}}
            >
              <OptionalTooltip
                enabled
                title={
                  subWorkstreamsExpanded ? t('hide_sub_workstreams') : t('show_sub_workstreams')
                }
                placement="top"
                wrapperStyle={{display: 'flex', alignItems: 'center'}}
              >
                <UIIcon
                  src={subWorkstreamsExpanded ? ChevronDownLine : ChevronRightLine}
                  className={atoms({color: 'Text Subtle'})}
                  size="small"
                />
              </OptionalTooltip>
            </button>
            {nameLink}
          </span>
        ) : (
          nameLink
        )
      }
    >
      {orderedMetrics.map((metric) => (
        <ScorecardMetricRow
          key={metric.id}
          grain={grain}
          metric={metric}
          year={year}
          quarter={quarter}
          workstreamId={workstream.id}
          handleMetricChange={(metricId, update) =>
            handleMetricChange(workstream.id, orderedMetrics, metricId, update)
          }
        />
      ))}
      {subWorkstreamsExpanded
        ? subWorkstreamMetricGroups.map((group) => (
            <Fragment key={group.id}>
              <SubWorkstreamHeaderRow name={group.name} columnCount={columnCount} />
              {group.metrics.map((metric) => (
                <ScorecardMetricRow
                  key={metric.id}
                  grain={grain}
                  metric={metric}
                  year={year}
                  quarter={quarter}
                  workstreamId={group.id}
                  handleMetricChange={(metricId, update) =>
                    handleMetricChange(group.id, group.metrics, metricId, update)
                  }
                />
              ))}
            </Fragment>
          ))
        : null}
    </TableGroupHeaderSection>
  );
};

/**
 * "Scorecard" rendering for a workstream list/pillar view: a single aligned table
 * grouped by workstream with a sticky column header and sticky per-workstream
 * group headers, matching the global Scorecards view. `headerControl` (e.g. the
 * timeframe menu) sits at the end of the Metric column's header.
 */
export const WorkstreamMetricsScorecardTable = ({
  grain,
  workstreams,
  year,
  quarter,
  includeArchived = false,
  headerControl,
}: {
  grain: MetricsGrain;
  workstreams: WorkstreamBasicWithSummary[];
  year: number;
  quarter: MetricQuarter;
  includeArchived?: boolean;
  headerControl?: ReactNode;
}) => {
  const isQuarterly = grain === 'quarterly';
  const columns = useMemo(
    () => (isQuarterly ? getQuarterMetricColumns() : ANNUAL_METRIC_COLUMNS),
    [isQuarterly]
  );
  const columnHeaderLabel = useMemo(
    () => (isQuarterly ? getQuarterColumnHeaderLabel(year, quarter) : getAnnualColumnHeaderLabel()),
    [isQuarterly, year, quarter]
  );
  const columnHeaderContent = useMemo(
    () =>
      headerControl
        ? {
            metric: (
              <Box display="flex" justifyContent="flex-end">
                {headerControl}
              </Box>
            ),
          }
        : undefined,
    [headerControl]
  );
  const flatData = useMemo(
    () => workstreams.flatMap((workstream) => onlyMetrics(workstream.metrics)),
    [workstreams]
  );

  return (
    <Box paddingX="16" paddingTop="16" {...tableStickyLayoutDataProps} style={PANE_STICKY_VARS}>
      <Table
        // A new grain swaps the column set, so it starts from fresh column widths.
        key={grain}
        columns={columns}
        data={flatData}
        bodyCellPadding={8}
        headerTextSize="small"
        columnHeaderLabel={columnHeaderLabel}
        columnHeaderContent={columnHeaderContent}
        horizontalLines
        border={false}
        resizable={isQuarterly}
        scrollMode="page"
        className={isQuarterly ? undefined : ANNUAL_TABLE_CLASS_NAME}
        style={{width: '100%'}}
      >
        <Table.Header />
        <Table.Body>
          {workstreams.map((workstream) => (
            <ScorecardGroup
              key={workstream.id}
              grain={grain}
              workstream={workstream}
              year={year}
              quarter={quarter}
              includeArchived={includeArchived}
            />
          ))}
        </Table.Body>
      </Table>
    </Box>
  );
};
